export enum ChatMessageRole {
    USER = 'user',
    ASSISTANT = 'assistant',
}

export interface BaseChatMessage {
    activityId: string;
    role: ChatMessageRole;
    content: string;
    timestamp: string;
}

export interface AssistantMessageFeedback {
    like: boolean;
    reason: string;
    comment: string;
    feedbackTimestamp: string;
}

export enum InformativeMessageStatus {
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    WAITING = 'WAITING',
    ERROR = 'ERROR',
}

export interface InformativeMessage {
    id: string;
    name: string;
    status: InformativeMessageStatus;
    message: string;
    parentId?: string;
    subInstructions?: string[];
    verbose?: boolean;
    error_message?: string;
}

export interface UserMessage extends BaseChatMessage {}

export enum CardType {
    FeedbackFormCard = 'FeedbackFormCard',
    FeedbackConfirmedCard = 'FeedbackConfirmedCard',
}

export interface BaseCardProps {
    timestamp: string;
}

export interface FeedbackFormCardProps extends BaseCardProps {
    feedback?: AssistantMessageFeedback;
    onSubmit?: (reason: string, comment: string) => void;
    onCancel?: () => void;
}

export interface ChatFlowCard<T extends BaseCardProps = BaseCardProps> {
    activityId: string;
    cardType: CardType;
    cardProps: T;
}

export interface AssistantMessage extends BaseChatMessage {
    informativeMessageMap: Record<string, InformativeMessage>;
    feedback?: AssistantMessageFeedback;
    // fields managed by frontend
    additionalCards: ChatFlowCard[];
    isStreaming?: boolean;
    isAuthorize?: boolean;
}

export interface ConversationSummary {
    conversationId: string;
    lastQuery: string;
    lastQueryTimestamp: string;
}

export interface Conversation {
    conversationId: string;
    messages: BaseChatMessage[];
}

export interface ChatMetadata {
    deep_think?: boolean;
}

// should keep sync with StreamChunkCategory in agent service
export enum StreamChunkContentType {
    TEXT = 'TEXT',
    CITATION = 'CITATION',
    INFORMATIVE_MESSAGE = 'INFORMATIVE_MESSAGE',
    IMAGE = 'IMAGE',
    AUDIO = 'AUDIO',
    VIDEO = 'VIDEO',
    DEBUG = 'DEBUG',
    INFO = 'INFO',
    WARNING = 'WARNING',
    ERROR = 'ERROR',
}

export interface StreamChunkContentDto<T = string> {
    content: T;
    contentType: StreamChunkContentType;
}

export interface StreamChunk {
    conversationId: string;
    activityId: string;
    responseStartTimestamp: string;
    replyToId: string;
    role: ChatMessageRole.ASSISTANT;
    contents: StreamChunkContentDto[];
    isAuthorize?: boolean;
}

export enum ChatApiAction {
    NEW = 'new',
    CHAT = 'chat',
}

export interface ChatApiPayload extends ChatMetadata {
    conversationId: string;
    activityId: string;
    query: string;
    userAction: ChatApiAction;
    role: ChatMessageRole.USER;
    contents: StreamChunkContentDto[];
    userSelection: string;
    channel: string;
}

export interface GetConversationSummaryPayload {
    pageSize?: number;
    beforeTimestamp: string;
    searchText: string;
}

export interface GetConversationSummaryResponse {
    conversationSummaries: ConversationSummary[];
    isLastPage: boolean;
}

export interface GetConversationHistoryPayload {
    conversationId: string;
}

export interface SetFeedbackPayload {
    conversationId: string;
    activityId: string;
    feedback: AssistantMessageFeedback;
}
